import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Slick from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import MagicSliderDots from 'react-magic-slider-dots';
import 'react-magic-slider-dots/dist/magic-dots.css';
import classNames from 'classnames';

import CardImage from '../CardImage';
import { wrapEventHandler } from '../../../utils/handlers';

import * as styles from './styles/styles.module.scss';

import CarouselArrowNext from '../../../../assets/images/icons/carouselArrowNext.svg';
import CarouselArrowPrev from '../../../../assets/images/icons/carouselArrowPrev.svg';

const ArrowFn = (key, bg) => {
  return ({ className, style, onClick }) => {
    return (
      <button
        aria-label={key}
        className={`${className} ${styles.button} ${styles[key]} lazyload`}
        data-bg={bg}
        onClick={onClick}
        style={style}
        title={key}
        type="button"
      />
    );
  };
};

const NextArrow = ArrowFn('next', CarouselArrowNext);
const PrevArrow = ArrowFn('previous', CarouselArrowPrev);

const appendDots = dots => {
  return <MagicSliderDots dots={dots} numDotsToShow={4} dotWidth={20} />;
};

const CardImageCarousel = ({
  handleBeforeChange,
  handleAfterChange,
  handleCardClick,
  className,
  classNameImage,
  useLowerButtons,
  photoUrls,
  slickSettings,
  title
}) => {
  const [dragging, setDragging] = useState(false);

  const beforeChange = (oldIndex, newIndex) => {
    if (handleBeforeChange) {
      handleBeforeChange(oldIndex, newIndex);
    }
    setDragging(true);
  };

  const afterChange = index => {
    if (handleAfterChange) {
      handleAfterChange(index);
    }
    setDragging(true);
  };

  const cardClick = () => {
    // Prevent propagation of click to inner elements when dragging slick carousel
    if (dragging) {
      return;
    }
    if (handleCardClick) {
      handleCardClick();
    }
  };

  const settings = {
    accessibility: true,
    afterChange,
    appendDots,
    beforeChange,
    className: classNames(className, styles.slickCarousel, { [styles.lowerButtons]: useLowerButtons }),
    dots: true,
    infinite: true,
    initialSlide: 0,
    lazyLoad: 'progressive',
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
    slidesToScroll: 1,
    slidesToShow: 1,
    speed: 300,
    swipeToSlide: true
  };

  if (slickSettings) {
    Object.assign(settings, slickSettings);
  }

  return (
    // A figure helps provide more semantic meaning for tests and accessibility.
    <figure>
      <Slick {...settings} accessibility>
        {photoUrls.map(photoUrl => (
          <CardImage className={classNameImage} key={photoUrl} lazyload={false} onClick={wrapEventHandler(cardClick)} url={photoUrl} title={title} />
        ))}
      </Slick>
    </figure>
  );
};

CardImageCarousel.defaultProps = {
  handleBeforeChange: null,
  handleAfterChange: null,
  handleCardClick: null,
  className: '',
  classNameImage: '',
  useLowerButtons: false,
  photoUrls: [],
  slickSettings: {},
  title: ''
};

CardImageCarousel.propTypes = {
  handleBeforeChange: PropTypes.func,
  handleAfterChange: PropTypes.func,
  handleCardClick: PropTypes.func,
  className: PropTypes.string,
  classNameImage: PropTypes.string,
  useLowerButtons: PropTypes.bool,
  photoUrls: PropTypes.arrayOf(PropTypes.string),
  slickSettings: PropTypes.object,
  title: PropTypes.string
};

export default CardImageCarousel;
